/**
 * Delimiter from a git branch name to parse the parent version it belongs to.
 * @example
 * `1.0.0_my-branch-name`
 */
const BRANCH_SPLIT_KEY = '_';
const VERSION_REGEX = /^v([0-9]+)(?:\.([0-9]+))?(?:\.([0-9]+))?(-.*)?$/;
const BRANCH_VALID_REGEX = /^(?!\/)(?!.*\/\.)(?![-_.])(?!.*[-_./]{2})(?!.*[-_.]$)[A-Za-z0-9._/-]+(?<!\/)(?<!\.lock)$/i;

/**
 * Whether the provided string contains a valid version and valid branch name.
 */
export const hasVersionBranch = (str = ''): boolean => {
  return str.includes(BRANCH_SPLIT_KEY);
};

/**
 * Parses the provided string and returns a hash with `version` and `branch`
 * name segments while preserving the `v` prefix if included.
 * @example
 * ```ts
 * parseVersionBranch('1.2.0')
 * // => { version: '1.2.0', branch: '' }
 * parseVersionBranch('1.2.0_cool-branch')
 * // => { version: '1.2.0', branch: '1.2.0_cool-branch' }
 * parseVersionBranch('v1.2.0_cool-branch')
 * // => { version: 'v1.2.0', branch: 'v1.2.0_cool-branch' }
 * ```
 */
export const parseVersionBranch = (
  str = '',
): {
  version: string;
  branch: string;
} => {
  const normalizedStr = str.trim();
  const [version, ...rest] = normalizedStr.split(BRANCH_SPLIT_KEY);
  return { version, branch: rest.length ? normalizedStr : '' };
};

/**
 * Determines if the provided string is a valid version name with a `v` prefix.
 */
export function isVersionName(str: string): boolean {
  return VERSION_REGEX.test(str.trim());
}

/**
 * Determines if the provided string is a valid branch name with a `v` prefix.
 */
export function isBranchName(str: string): boolean {
  const { version, branch } = parseVersionBranch(str);

  const isValidBranch = !!branch && !!branch.trim().match(BRANCH_VALID_REGEX);
  return isVersionName(version) && !!isValidBranch;
}

/**
 * Returns the provided string prefixed with `v` if not already prefixed.
 *
 * @example v1.0 -> v1.0
 * @example 1.0 => v1.0
 * @example 1.0_child-branch -> v1.0_child-branch
 */
export function branchNameWithPrefix(str: string): string {
  return str.startsWith('v') ? str : `v${str}`;
}

/**
 * Returns the provided string without a `v` prefix, if already prefixed.
 *
 * @example v1.0 -> 1.0
 * @example 1.0 => 1.0
 * @example v1.0_child-branch -> 1.0_child-branch
 */
export function branchNameWithoutPrefix(str: string): string {
  return str.startsWith('v') ? str.slice(1) : str;
}

export * from './diff';
export * from './mock';
export * from './review';
